import React from 'react';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { ScrollArea } from '../ui/scroll-area';
import { 
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../ui/select';
import { Switch } from '../ui/switch';
import { Filter, RotateCcw } from 'lucide-react';

const US_STATES = [
    "Alabama", "Alaska", "Arizona", "Arkansas", "California", "Colorado",
    "Connecticut", "Delaware", "Florida", "Georgia", "Hawaii", "Idaho",
    "Illinois", "Indiana", "Iowa", "Kansas", "Kentucky", "Louisiana",
    "Maine", "Maryland", "Massachusetts", "Michigan", "Minnesota",
    "Mississippi", "Missouri", "Montana", "Nebraska", "Nevada",
    "New Hampshire", "New Jersey", "New Mexico", "New York",
    "North Carolina", "North Dakota", "Ohio", "Oklahoma", "Oregon",
    "Pennsylvania", "Rhode Island", "South Carolina", "South Dakota",
    "Tennessee", "Texas", "Utah", "Vermont", "Virginia", "Washington",
    "West Virginia", "Wisconsin", "Wyoming", "National"
];

const ORGANIZATION_TYPES = [
    "Employer",
    "Training Provider",
    "Service Provider",
    "Support Organization",
    "Government",
    "Foundation or Private Funder",
    "Higher Education",
    "Parents & Caregivers",
    "Workforce Alliance"
];

const FilterSidebar = ({ 
    filters, 
    onFilterChange, 
    onClearFilters,
    className = '' 
}) => {
    const { state, organizationType, featuredOnly } = filters;

    const handleStateChange = (value) => {
        onFilterChange({ ...filters, state: value === 'all' ? '' : value });
    };

    const handleTypeChange = (type, checked) => {
        onFilterChange({ ...filters, organizationType: checked ? type : '' });
    };

    const handleFeaturedChange = (checked) => {
        onFilterChange({ ...filters, featuredOnly: checked });
    };

    const hasActiveFilters = state || organizationType || featuredOnly;

    return (
        <div className={`bg-card border border-border rounded-lg p-4 ${className}`} data-testid="filter-sidebar">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                    <Filter className="h-4 w-4 text-muted-foreground" />
                    <h3 className="font-heading font-semibold text-sm">Filters</h3>
                </div>
                {hasActiveFilters && (
                    <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={onClearFilters}
                        className="h-8 px-2 text-xs"
                        data-testid="clear-filters-btn"
                    >
                        <RotateCcw className="h-3 w-3 mr-1" />
                        Clear
                    </Button>
                )}
            </div>

            {/* State Filter */}
            <div className="mb-6">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground mb-2 block">
                    State / Region
                </Label>
                <Select value={state || 'all'} onValueChange={handleStateChange}>
                    <SelectTrigger className="w-full" data-testid="state-filter-select">
                        <SelectValue placeholder="All States" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All States</SelectItem>
                        {US_STATES.map((s) => (
                            <SelectItem key={s} value={s}>{s}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            {/* Organization Type Filter */}
            <div className="mb-6">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground mb-3 block">
                    Organization Type
                </Label>
                <ScrollArea className="h-[200px] pr-4">
                    <div className="space-y-1">
                        {ORGANIZATION_TYPES.map((type) => (
                            <button
                                key={type}
                                type="button"
                                onClick={() => handleTypeChange(type, organizationType !== type)}
                                className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-left transition-colors duration-150 ${
                                    organizationType === type 
                                        ? 'bg-primary/10 text-primary' 
                                        : 'hover:bg-muted'
                                }`}
                                data-testid={`filter-type-${type.toLowerCase().replace(/\s+/g, '-')}`}
                            >
                                <div className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${
                                    organizationType === type 
                                        ? 'bg-primary border-primary' 
                                        : 'border-input'
                                }`}>
                                    {organizationType === type && (
                                        <svg className="w-3 h-3 text-primary-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                        </svg>
                                    )}
                                </div>
                                <span className="text-sm">{type}</span>
                            </button>
                        ))}
                    </div>
                </ScrollArea>
            </div>

            {/* Featured Filter */}
            <div className="pt-4 border-t border-border">
                <div className="flex items-center justify-between">
                    <Label 
                        htmlFor="featured-only"
                        className="text-sm font-normal cursor-pointer"
                    >
                        Featured only
                    </Label>
                    <Switch 
                        id="featured-only"
                        checked={featuredOnly}
                        onCheckedChange={handleFeaturedChange}
                        data-testid="filter-featured-only"
                    />
                </div>
            </div>
        </div>
    );
};

export default FilterSidebar;
